import {FeatureFlag} from '../../../@type/features';
import {onCheckoutSubmitErrorManual, onCheckoutSubmitManual} from '../checkout';
import {Feature} from '../features';
import {AuthnetButtonElement} from './class-authnet-element';
import {$qsAll} from '../../../@shared/ts/dom';
import {insertCaptchaFields, cleanupCaptchaFields} from '../botProtection';
import {cleanupTransactionFields} from '../transaction';

async function initAuthnetPayments($checkoutForm: HTMLFormElement): Promise<void> {
	if (!Feature.enabled(FeatureFlag.AUTHNET_GATEWAY)) {
		return;
	}

	// Classic checkout AcceptUI v3 renders its own hosted button and hidden
	// opaqueData fields, so WooCommerce must keep its normal submit pipeline.
	const hasInlineCardForm = Boolean(document.querySelector('.pp-authnet-card-fields'));
	const hasClassicAcceptUI = Boolean(
		document.querySelector('.pp-authnet-acceptui,.pp-authnet-acceptui-button')
		?? $checkoutForm.querySelector('input[name="peachpay_authnet_data_descriptor"],input[name="peachpay_authnet_data_value"]'),
	);
	const gateways = [
		...(hasInlineCardForm || hasClassicAcceptUI ? [] : ['peachpay_authnet_card']),
		'peachpay_authnet_echeck',
	];

	const loginId = Feature.metadata<string>(FeatureFlag.AUTHNET_GATEWAY, 'login_id');
	const clientKey = Feature.metadata<string>(FeatureFlag.AUTHNET_GATEWAY, 'public_client_key');
	const acceptSrc = Feature.metadata<string>(FeatureFlag.AUTHNET_GATEWAY, 'accept_src');

	if (!clientKey || !loginId || !acceptSrc) {
		return;
	}

	const element = new AuthnetButtonElement({
		loginId,
		clientKey,
		acceptSrc,
	});

	onCheckoutSubmitManual(
		$checkoutForm,
		gateways,
		async gatewayId => {
			const type = gatewayId === 'peachpay_authnet_card' ? 'card' : 'bank';

			cleanupAuthnetCheckoutFields();

			const {token, error, cancel} = await element.tokenize(type);

			if (cancel) {
				return false;
			}

			if (error ?? !token) {
				const target = '.payment_box:not([style*="display:none"]):not([style*="display: none"])';

				$qsAll(`${target} .peachpay-tokenize-error`, $el => {
					$el.classList.remove('hide');
					$el.innerHTML = error?.map(error => `${error.code}: ${error.message}`).join('\n') ?? 'An unknown error occured while tokenizing the payment method.';
				});

				setTimeout(() => {
					$qsAll('.peachpay-tokenize-error', $el => {
						$el.classList.add('hide');
						$el.innerHTML = '';
					});
				}, 10000);

				return false;
			}

			prepareAuthnetTokenizedCheckout($checkoutForm, token.descriptor, token.value);
			await insertCaptchaFields($checkoutForm);

			return true;
		});

	onCheckoutSubmitErrorManual($checkoutForm, gateways, async () => {
		cleanupAuthnetCheckoutFields();
	});
}

function cleanupAuthnetCheckoutFields() {
	cleanupTransactionFields();
	cleanupTokenFields();
	cleanupCaptchaFields();
}

function prepareAuthnetTokenizedCheckout($checkoutForm: HTMLFormElement, dataDescriptor: string, dataValue: string) {
	cleanupAuthnetCheckoutFields();
	insertTokenFields($checkoutForm, dataDescriptor, dataValue);
}

function insertTokenFields($checkoutForm: HTMLFormElement, dataDescriptor: string, dataValue: string) {
	const $dataDescriptor = document.createElement('input');
	$dataDescriptor.type = 'hidden';
	$dataDescriptor.name = 'peachpay_authnet_data_descriptor';
	$dataDescriptor.value = dataDescriptor;
	$checkoutForm.insertAdjacentElement('beforeend', $dataDescriptor);

	const $dataValue = document.createElement('input');
	$dataValue.type = 'hidden';
	$dataValue.name = 'peachpay_authnet_data_value';
	$dataValue.value = dataValue;
	$checkoutForm.insertAdjacentElement('beforeend', $dataValue);
}

function cleanupTokenFields() {
	$qsAll('input[name="peachpay_authnet_data_descriptor"]', $el => {
		$el.remove();
	});

	$qsAll('input[name="peachpay_authnet_data_value"]', $el => {
		$el.remove();
	});
}

export {
	initAuthnetPayments,
	prepareAuthnetTokenizedCheckout,
};
